import { create } from "zustand";
import type { Analysis, Arrangement, ChartMeasure, SongSource } from "../types/domain";
import { buildChartModel } from "../core/grid/buildChartModel";
import { headerTempo } from "../core/grid/tempo";
import { fitFixedGrid, type FixedGrid } from "../core/grid/fixedGrid";

interface ProjectState {
  title: string;
  // what to call the song: the YouTube video title once the player reports it, the file name for uploads
  displayTitle: string;
  setDisplayTitle: (t: string) => void;
  analysis: Analysis | null;
  source: SongSource;
  beatLevelMul: 0.5 | 1 | 2;
  arrangement: Arrangement;   // legacy (v1 editor notes)

  // derived (recomputed by rebuild)
  chartMeasures: ChartMeasure[];
  headerTempo: number | null;
  tempoGrid: FixedGrid | null;   // fixed-tempo blocks fitted to the beats (metronome + tempo markers)
  musicStartBar: number;         // first bar of the music core (a PDF sheet without chords starts here)

  loadAnalysis: (analysis: Analysis, source: SongSource, title: string) => void;
  setBeatLevelMul: (mul: 0.5 | 1 | 2) => void;
  setArrangement: (arr: Arrangement) => void;
  reset: () => void;
  rebuild: () => void;
}

export const useProjectStore = create<ProjectState>((set, get) => ({
  title: "",
  displayTitle: "",
  setDisplayTitle: (displayTitle) => set({ displayTitle }),
  analysis: null,
  source: { kind: "default" },
  beatLevelMul: 1,
  arrangement: { measures: {} },
  chartMeasures: [],
  headerTempo: null,
  tempoGrid: null,
  musicStartBar: 0,

  loadAnalysis: (analysis, source, title) => {
    set({ analysis, source, title, displayTitle: source.kind === "youtube" ? "" : title, beatLevelMul: 1, arrangement: { measures: {} } });
    get().rebuild();
  },
  setBeatLevelMul: (mul) => { set({ beatLevelMul: mul }); get().rebuild(); },
  setArrangement: (arrangement) => { set({ arrangement }); get().rebuild(); },
  reset: () => set({
    title: "", displayTitle: "", analysis: null, source: { kind: "default" }, beatLevelMul: 1,
    arrangement: { measures: {} }, chartMeasures: [], headerTempo: null, tempoGrid: null, musicStartBar: 0,
  }),

  rebuild: () => {
    const { analysis, beatLevelMul } = get();
    if (!analysis) { set({ chartMeasures: [], headerTempo: null, tempoGrid: null }); return; }
    const { measures, empty, musicStartBar } = buildChartModel(analysis, beatLevelMul);
    if (empty) { set({ chartMeasures: [], headerTempo: null, tempoGrid: null, musicStartBar: 0 }); return; }
    set({ chartMeasures: measures, headerTempo: headerTempo(measures, analysis.bpm), tempoGrid: fitFixedGrid(measures), musicStartBar });
  },
}));
